import {
    Box,
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Text,
} from '@chakra-ui/react';
import { useTranslation } from 'helpers/languages/translations';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { linkStyle } from 'shared/styles';
import { TColorsHooks } from '../useColors';
import useModal from './useModal';
import ColorPicker from '../ColorPicker';

interface IProps {
    colorToSubmit: TColorsHooks['colorToSubmit'];
    setColorToSubmit: TColorsHooks['setColorToSubmit'];
    selectedType: TColorsHooks['selectedType'];
    unsetColorsData: TColorsHooks['unsetColorsData'];
    showColorPicker: TColorsHooks['showColorPicker'];
    setShowColorPicker: TColorsHooks['setShowColorPicker'];
    setIsModalOpen: TColorsHooks['setIsModalOpen'];
    isModalOpen: TColorsHooks['isModalOpen'];
    modalType: TColorsHooks['modalType'];
}

const ColorsModal = ({
    colorToSubmit,
    setColorToSubmit,
    unsetColorsData,
    selectedType,
    showColorPicker,
    setShowColorPicker,
    setIsModalOpen,
    isModalOpen,
    modalType,
}: IProps) => {
    const {
        isAddColorLoading,
        isUpdateColorLoading,
        handleDeleteKeyword,
        handleCloseModal,
        handleSubmit,
        handleObjectIdChange,
        changeColor,
        updateInitialRef,
    } = useModal({ colorToSubmit, setColorToSubmit, setIsModalOpen, modalType });

    const translate = useTranslation();
    return (
        <Modal
            initialFocusRef={updateInitialRef}
            isOpen={isModalOpen}
            onClose={() => {
                handleCloseModal();
            }}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader tabIndex={-1} ref={updateInitialRef}>
                    {translate('colors.modal.title')} / {translate(`colors.type.${selectedType}`)}
                </ModalHeader>
                <ModalCloseButton mt="2" isDisabled={modalType === 'add' ? isAddColorLoading : isUpdateColorLoading} />

                <ModalBody>
                    <Text w="30%" mb="8px" fontSize="sm">
                        {translate('colors.modal.field.name')}
                    </Text>
                    <Flex flex="1" mb="30px">
                        <Select
                            size="sm"
                            onChange={(e) => handleObjectIdChange(Number(e.target.value))}
                            value={colorToSubmit?.object_id}
                            isDisabled={modalType === 'update'}
                        >
                            {modalType === 'add' ? (
                                unsetColorsData?.colors.map((colorItem) => (
                                    <option key={colorItem.object_id} value={colorItem.object_id}>
                                        {colorItem.name}
                                    </option>
                                ))
                            ) : (
                                <option value={colorToSubmit?.object_id}>{colorToSubmit?.name}</option>
                            )}
                        </Select>
                    </Flex>

                    <Text w="30%" mb="8px" fontSize="sm">
                        {translate('colors.modal.field.color')}
                    </Text>
                    <ColorPicker
                        showColorPicker={showColorPicker}
                        setShowColorPicker={setShowColorPicker}
                        colorToSubmit={colorToSubmit}
                        changeColor={changeColor}
                    />
                </ModalBody>
                <ModalFooter fontSize="sm" justifyContent="space-between">
                    {modalType === 'update' && (
                        <Box
                            {...linkStyle}
                            color="red.500"
                            onClick={intentionalyFloatingPromiseReturn(handleDeleteKeyword)}
                        >
                            {translate('colors.modal.button.delete')}
                        </Box>
                    )}

                    <Flex flex="1" justifyContent="flex-end">
                        <Button
                            isDisabled={modalType === 'add' ? isAddColorLoading : isUpdateColorLoading}
                            size="sm"
                            variant="outline"
                            mr={3}
                            onClick={handleCloseModal}
                        >
                            {translate('colors.modal.button.cancel')}
                        </Button>
                        <Button
                            onClick={intentionalyFloatingPromiseReturn(handleSubmit)}
                            isLoading={modalType === 'add' ? isAddColorLoading : isUpdateColorLoading}
                            size="sm"
                            colorScheme="blue"
                            bg="blueMain"
                        >
                            {translate('colors.modal.button.confirm')}
                        </Button>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ColorsModal;
